import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { CodeXmlIcon, GripVerticalIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Button, Input } from '@tinct/web-components';
import { cn } from '@tinct/core-utils';
import type { CreateCustomCode, ReadCustomCode } from '@tinct/core-api-models';
import {
  useCreateCustomCode,
  useDeleteCustomCode,
  useGetCustomCodes,
  usePatchCustomCode,
  useReorderCustomCodes,
  type CustomCodeScope,
} from '@tinct/core-api';
import { customCodePlacementLabels } from './custom-code-placement';
import { CustomCodeModal } from './custom-code-modal';

export type CustomCodesPanelProps = {
  /** Workspace or page scope — same UI, different endpoints. */
  scope: CustomCodeScope;
  /** Extra line under the header (e.g. the workspace-wide summary on a page). */
  summary?: ReactNode;
  className?: string;
};

/**
 * The shared custom-code manager: ordered entries with drag-and-drop, filter,
 * enable state, and the create/edit modal. Entries are raw HTML injected into
 * every hosted page of the scope; the tinct analytics snippet is automatic
 * and never listed here.
 */
export function CustomCodesPanel({ scope, summary, className }: CustomCodesPanelProps) {
  const { data: codes, isLoading } = useGetCustomCodes(scope);

  const [filter, setFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<ReadCustomCode | null>(null);
  // Optimistic row order while the reorder round-trip is in flight.
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);

  const createMutation = useCreateCustomCode({
    onSuccess: () => setModalOpen(false),
    onError: (error) => toast.error(error.error),
  });
  const patchMutation = usePatchCustomCode({
    onSuccess: () => setModalOpen(false),
    onError: (error) => toast.error(error.error),
  });
  const deleteMutation = useDeleteCustomCode({
    onError: (error) => toast.error(error.error),
  });
  const reorderMutation = useReorderCustomCodes({
    onSettled: () => setPendingOrder(null),
    onError: (error) => toast.error(error.error),
  });

  const orderedCodes = useMemo(() => {
    const list = codes ?? [];
    if (!pendingOrder) return list;
    const byId = new Map(list.map((code) => [code.id, code]));
    return pendingOrder
      .map((id) => byId.get(id))
      .filter((code): code is ReadCustomCode => !!code);
  }, [codes, pendingOrder]);

  const filteredCodes = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return orderedCodes;
    return orderedCodes.filter((code) =>
      (code.name ?? '').toLowerCase().includes(query),
    );
  }, [orderedCodes, filter]);

  // Reordering a filtered subset would be ambiguous — drag only on the full list.
  const isFiltering = filter.trim().length > 0;
  const sortableIds = filteredCodes.map((code) => code.id ?? '');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedCodes.map((code) => code.id ?? '');
    const from = ids.indexOf(active.id as string);
    const to = ids.indexOf(over.id as string);
    if (from < 0 || to < 0) return;
    const next = arrayMove(ids, from, to);
    setPendingOrder(next);
    reorderMutation.mutate({ scope, ids: next });
  };

  const handleAdd = () => {
    setEditingCode(null);
    setModalOpen(true);
  };

  const handleEdit = (code: ReadCustomCode) => {
    setEditingCode(code);
    setModalOpen(true);
  };

  const handleSave = (data: CreateCustomCode) => {
    if (editingCode?.id) {
      patchMutation.mutate({ scope, codeId: editingCode.id, data });
    } else {
      createMutation.mutate({ scope, data });
    }
  };

  const handleDelete = (code: ReadCustomCode) => {
    if (!code.id) return;
    deleteMutation.mutate({ scope, codeId: code.id });
  };

  const saveError =
    (editingCode ? patchMutation.error?.error : createMutation.error?.error) ??
    null;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter…"
          className="max-w-56"
        />
        <div className="flex-1" />
        <Button size="sm" onClick={handleAdd}>
          <PlusIcon className="size-4" />
          Add
        </Button>
      </div>

      {summary}

      {/* List */}
      {isLoading ? (
        <div className="px-2 py-6 text-sm text-muted-foreground">Loading…</div>
      ) : filteredCodes.length === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          {isFiltering
            ? 'No entry matches your filter.'
            : 'No custom code yet. Add scripts, styles or structured data — they load on every published page of this scope.'}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <ul className="flex flex-col rounded-lg border">
              {filteredCodes.map((code) => (
                <CustomCodeRow
                  key={code.id}
                  code={code}
                  dragDisabled={isFiltering}
                  onEdit={() => handleEdit(code)}
                  onDelete={() => handleDelete(code)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <p className="text-xs text-muted-foreground">
        The preview reflects changes immediately. Changes apply to your live
        pages the next time you publish.
      </p>

      <CustomCodeModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        code={editingCode}
        errorMessage={modalOpen ? saveError : null}
        isSaving={createMutation.isPending || patchMutation.isPending}
        onSave={handleSave}
      />
    </div>
  );
}

type CustomCodeRowProps = {
  code: ReadCustomCode;
  dragDisabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

function CustomCodeRow({ code, dragDisabled, onEdit, onDelete }: CustomCodeRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: code.id ?? '', disabled: dragDisabled || !code.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex cursor-pointer items-center gap-3 border-b px-3 py-3 last:border-b-0 hover:bg-muted/50',
        isDragging && 'z-10 bg-background shadow-sm',
      )}
      onClick={onEdit}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        aria-label="Reorder"
        className={cn(
          'cursor-grab text-muted-foreground/60 hover:text-foreground',
          dragDisabled && 'invisible',
        )}
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <GripVerticalIcon className="size-4" />
      </button>

      <CodeXmlIcon className="size-4 shrink-0 text-muted-foreground" />

      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
        {code.name}
      </span>

      {code.enabled === false && <Badge variant="secondary">Disabled</Badge>}

      <span className="shrink-0 font-mono text-xs text-muted-foreground">
        {code.placement ? customCodePlacementLabels[code.placement] : ''}
      </span>

      <Button
        variant="ghost"
        size="icon"
        aria-label="Delete"
        className="size-8 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <Trash2Icon className="size-4" />
      </Button>
    </li>
  );
}
